import { formatTRY } from "@/lib/currency";
import { createFileRoute } from "@tanstack/react-router";
import { Armchair, Clock, DollarSign, Receipt, Wallet } from "lucide-react";

import {
  AlertsList,
  PaymentBreakdown,
  RevenueChart,
  StatTile,
  TopProductsList,
} from "@/components/admin/overview-widgets";
import { useCart } from "@/components/dashboard/cart-context";
import { authClient } from "@/lib/auth-client";
import { memberHasPermission, StaffPermission } from "@pos-vidyon/api/permissions";
import { useQuery } from "@tanstack/react-query";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/admin/")({
  component: AdminOverview,
});

const weeklyRevenue = [
  { label: "Pzt", value: 1820 },
  { label: "Sal", value: 2140 },
  { label: "Çrş", value: 1990 },
  { label: "Prş", value: 2430 },
  { label: "Cum", value: 2810 },
  { label: "Cmt", value: 3350 },
  { label: "Bugün", value: 2384, isToday: true },
];

const topProducts = [
  { name: "Espresso", units: 142 },
  { name: "Margherita Pizza", units: 98 },
  { name: "Cappuccino", units: 87 },
  { name: "Latte", units: 76 },
  { name: "Cheesecake", units: 54 },
];

const paymentBreakdown = [
  { label: "Kart", value: 62, barClassName: "bg-gray-900", dotClassName: "bg-gray-900" },
  { label: "Nakit", value: 28, barClassName: "bg-emerald-600", dotClassName: "bg-emerald-600" },
  { label: "Online / QR", value: 10, barClassName: "bg-amber-500", dotClassName: "bg-amber-500" },
];

const alerts: { severity: "warning" | "critical" | "info"; text: string }[] = [
  { severity: "warning", text: "Espresso çekirdeği stoku azaldı (%12 kaldı)" },
  { severity: "critical", text: "Mutfak yazıcısı (KOT-2) bağlantısı kesildi" },
  { severity: "info", text: "B-04 masası 45 dakikadır hesap bekliyor" },
];

const dateRangePresets = ["Bugün", "Son 7 Gün", "Son 30 Gün", "Bu Ay"];

function AdminOverview() {
  const { data: activeMember } = authClient.useActiveMember();
  const cart = useCart();

  const { data: customRoles = [] } = useQuery(orpc.restaurant.getStaffRoles.queryOptions());

  const canViewRevenue = activeMember
    ? memberHasPermission(activeMember, StaffPermission.VIEW_REVENUE, customRoles)
    : false;

  const occupiedTables = cart.tables.filter((t) => t.status === "occupied");
  const occupancyRate = Math.round((occupiedTables.length / cart.tables.length) * 100);

  const liveActivity = occupiedTables
    .map((table) => {
      const items = cart.tablesOrderItems[table.id] ?? [];
      const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      return { ...table, total: subtotal * 1.08 };
    })
    .slice(0, 6);

  return (
    <div className="space-y-4 pb-4">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900">Genel Bakış</h1>
          <p className="text-sm text-gray-500">İşletmenin bugünkü performansına hızlı bir bakış.</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
          {dateRangePresets.map((preset, i) => (
            <button
              key={preset}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition-all ${
                i === 0 ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div
        className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${canViewRevenue ? "xl:grid-cols-4" : "xl:grid-cols-2"}`}
      >
        {canViewRevenue && (
          <StatTile
            label="Bugünkü Ciro"
            value="2.384 ₺"
            delta="+12.4%"
            deltaGood
            trend={[1200, 1400, 1100, 1600, 1800, 2000, 2384]}
            icon={DollarSign}
            iconClassName="bg-gray-50 text-gray-900"
          />
        )}
        <StatTile
          label="Sipariş Sayısı"
          value="86"
          delta="+5 sipariş"
          deltaGood
          trend={[60, 68, 72, 65, 80, 78, 86]}
          icon={Receipt}
          iconClassName="bg-emerald-50 text-emerald-600"
        />
        {canViewRevenue && (
          <StatTile
            label="Ortalama Sepet"
            value="27,72 ₺"
            delta="-1.8%"
            deltaGood={false}
            trend={[29, 28.5, 30, 27, 26.8, 28, 27.72]}
            icon={Wallet}
            iconClassName="bg-amber-50 text-amber-600"
          />
        )}
        <StatTile
          label="Aktif Masalar"
          value={`${occupiedTables.length} / ${cart.tables.length}`}
          delta={`%${occupancyRate} doluluk`}
          deltaGood={occupancyRate < 70}
          icon={Armchair}
          iconClassName="bg-rose-50 text-rose-600"
        />
      </div>

      {/* Main content grid */}
      <div className="flex flex-col gap-4 xl:flex-row">
        {/* Left column */}
        <div className="flex flex-[2] flex-col gap-4 min-w-0">
          {canViewRevenue && (
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-800">Haftalık Ciro</h2>
                <span className="text-[11px] font-semibold text-gray-400">Son 7 gün</span>
              </div>
              <div className="mt-3">
                <RevenueChart data={weeklyRevenue} />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-4 sm:flex-row">
            <div
              className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${canViewRevenue ? "flex-1" : "w-full"}`}
            >
              <h2 className="text-sm font-bold text-gray-800">En Çok Satan Ürünler</h2>
              <div className="mt-4">
                <TopProductsList items={topProducts} />
              </div>
            </div>
            {canViewRevenue && (
              <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="text-sm font-bold text-gray-800">Ödeme Yöntemleri</h2>
                <div className="mt-4">
                  <PaymentBreakdown data={paymentBreakdown} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-1 flex-col gap-4 min-w-0">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-800">Canlı Masa Aktivitesi</h2>
              <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Canlı
              </span>
            </div>
            <div className="mt-3 space-y-2">
              {liveActivity.map((table) => (
                <div
                  key={table.id}
                  className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 p-2.5 text-xs shadow-sm"
                >
                  <div className="space-y-0.5">
                    <p className="font-bold text-gray-800">Masa {table.name}</p>
                    <p className="flex items-center gap-1 text-[10px] text-gray-400">
                      <Clock className="size-3" />
                      {table.area} • {table.floor}
                    </p>
                  </div>
                  <p className="font-extrabold text-rose-600">{formatTRY(table.total)}</p>
                </div>
              ))}
              {liveActivity.length === 0 && (
                <p className="py-4 text-center text-[11px] text-gray-400">Şu anda dolu masa yok.</p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-bold text-gray-800">Hızlı Uyarılar</h2>
            <div className="mt-3">
              <AlertsList alerts={alerts} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
